import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Icon from '@mui/material/Icon'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import React from 'react'
import { GoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useSelector } from 'react-redux'
import { z } from 'zod'

import Dialog from '@/app/component/atom/dialog'
import DialogHeader from '@/app/component/atom/dialog-header'
import { Form } from '@/app/component/form'
import SubmitButton from '@/app/component/form/submit-button'
import { useConfirmation } from '@/app/contexts/confirmation.context'
import { useSignin } from '@/app/module/auth/hooks/use-signin'
import { useVerifyOTP } from '@/app/module/auth/hooks/use-verify-otp'
import {
  completeReauthentication,
  logoutUser,
  selectOTPToken,
  selectReauthUsername,
  setOtp,
} from '@/app/module/auth/store/session.slice'
import { useAppDispatch } from '@/app/store/redux-hooks'

type Props = {}

type ReauthStep = 'otp' | 'password' | 'recovery'

type PasswordFormValues = {
  password: string
}

type OTPFormValues = {
  otp: string
}

type RecoveryCodeFormValues = {
  recoveryCode: string
}

const ReAuthDialog: React.FC<Props> = () => {
  const dispatch = useAppDispatch()
  const createConfirmation = useConfirmation()
  const [signin, { isLoading: isSigninLoading }] = useSignin()
  const [verifyOTP, { isLoading: isVerifyLoading }] = useVerifyOTP()

  const reauthUsername = useSelector(selectReauthUsername)
  const isReauthRequired = !!reauthUsername
  const otpToken = useSelector(selectOTPToken)

  const [captchaDisplayed, setCaptchaDisplayed] = React.useState(false)
  const [pendingPassword, setPendingPassword] = React.useState('')
  const [step, setStep] = React.useState<ReauthStep>('password')

  React.useEffect(() => {
    if (isReauthRequired) {
      setCaptchaDisplayed(false)
      setPendingPassword('')
      setStep('password')
    }
  }, [isReauthRequired])

  const handleComplete = React.useCallback(() => {
    dispatch(completeReauthentication())
    dispatch(setOtp(null))
  }, [dispatch])

  const handleSuccessfulReauthentication = React.useCallback(async () => {
    handleComplete()

    await createConfirmation({
      title: 'Re-authentication completed',
      content: "Welcome back! Please try again what you've been doing, e.g. saving the campaign.",
      confirmButtonLabel: 'OK',
      hideCancel: true,
    })
  }, [createConfirmation, handleComplete])

  const handleCloseRequest = React.useCallback(async () => {
    const confirmed = await createConfirmation({
      title: 'Cancel re-authentication',
      content: 'Are you sure you want to close this dialog? You will be signed out.',
      confirmButtonLabel: 'Sign out',
      cancelButtonLabel: 'Stay',
    })

    if (confirmed) {
      dispatch(logoutUser())
      handleComplete()
    }
  }, [createConfirmation, dispatch, handleComplete])

  const handlePasswordSubmit = React.useCallback((data: PasswordFormValues) => {
    setPendingPassword(data.password)
    setCaptchaDisplayed(true)
  }, [])

  const handleCaptchaVerify = React.useCallback(
    async (verifyToken: string) => {
      setCaptchaDisplayed(false)

      if (!pendingPassword || !reauthUsername) {
        return
      }

      const result = await signin({
        password: pendingPassword,
        username: reauthUsername,
        verifyToken,
      })

      if (!result) {
        return
      }

      if (result.totpEnabled) {
        setStep('otp')
        return
      }

      await handleSuccessfulReauthentication()
    },
    [handleSuccessfulReauthentication, pendingPassword, reauthUsername, signin],
  )

  const handleOTPSubmit = React.useCallback(
    async (data: OTPFormValues) => {
      const result = await verifyOTP({ passcode: data.otp }, otpToken)

      if (result) {
        await handleSuccessfulReauthentication()
      }
    },
    [handleSuccessfulReauthentication, otpToken, verifyOTP],
  )

  const handleRecoverySubmit = React.useCallback(
    async (data: RecoveryCodeFormValues) => {
      const result = await verifyOTP({ recovery: data.recoveryCode }, otpToken)

      if (result) {
        await handleSuccessfulReauthentication()
      }
    },
    [handleSuccessfulReauthentication, otpToken, verifyOTP],
  )

  const title = React.useMemo(() => {
    switch (step) {
      case 'otp':
      case 'recovery':
        return 'Two-factor authentication'
      default:
        return 'Login'
    }
  }, [step])

  if (!isReauthRequired) {
    return null
  }

  return (
    <Dialog open={isReauthRequired} fullWidth maxWidth="xs" onClose={handleCloseRequest}>
      <DialogHeader title={title} onClose={handleCloseRequest} />
      <DialogContent sx={{ paddingTop: 3 }}>
        <Typography sx={{ marginBottom: 1, textAlign: 'center' }}>
          <Icon color="primary" sx={{ fontSize: '4rem !important' }}>
            account_circle
          </Icon>
        </Typography>

        {step === 'password' && (
          <>
            <Typography color="textSecondary" variant="caption" sx={{ marginBottom: 2, display: 'block' }}>
              Please enter your password
            </Typography>
            <TextField
              fullWidth
              disabled
              label="Email"
              value={reauthUsername || ''}
              variant="standard"
              sx={{ mb: 2 }}
            />
            <Form
              defaultValues={defaultPasswordValues}
              fields={passwordFields}
              schema={passwordSchema}
              shouldReset={false}
              onSubmit={handlePasswordSubmit}
              renderActions={({ isDirty }) => (
                <DialogActions sx={{ marginTop: 2, paddingX: 0 }}>
                  <SubmitButton isDirty={isDirty} isSubmitting={isSigninLoading}>
                    Continue
                  </SubmitButton>
                </DialogActions>
              )}
            />
          </>
        )}
        {step === 'otp' && (
          <>
            <Typography color="textSecondary" variant="caption" sx={{ marginBottom: 2, display: 'block' }}>
              Please enter your two-factor token
            </Typography>
            <Form
              defaultValues={defaultOTPValues}
              fields={otpFields}
              schema={otpSchema}
              shouldReset={false}
              onSubmit={handleOTPSubmit}
              renderActions={({ isDirty }) => (
                <DialogActions sx={{ justifyContent: 'space-between', marginTop: 2, paddingX: 0 }}>
                  <Button color="primary" size="small" onClick={() => setStep('recovery')}>
                    Verify using a recovery code instead
                  </Button>
                  <SubmitButton isDirty={isDirty} isSubmitting={isVerifyLoading}>
                    Verify
                  </SubmitButton>
                </DialogActions>
              )}
            />

            <Typography color="textSecondary" variant="caption" sx={{ marginTop: 2, display: 'block' }}>
              You have enabled two-factor authentication for this account. Please open your authenticator app (Google
              Authenticator, Authy, etc) to retrieve a code.
            </Typography>
          </>
        )}
        {step === 'recovery' && (
          <>
            <Typography color="textSecondary" variant="caption" sx={{ marginBottom: 2, display: 'block' }}>
              Please enter your recovery code
            </Typography>
            <Form
              defaultValues={defaultRecoveryValues}
              fields={recoveryFields}
              schema={recoverySchema}
              shouldReset={false}
              onSubmit={handleRecoverySubmit}
              renderActions={({ isDirty }) => (
                <DialogActions sx={{ justifyContent: 'space-between', marginTop: 2, paddingX: 0 }}>
                  <Button color="primary" size="small" onClick={() => setStep('otp')}>
                    Verify using a two-factor token instead
                  </Button>
                  <SubmitButton isDirty={isDirty} isSubmitting={isVerifyLoading}>
                    Verify
                  </SubmitButton>
                </DialogActions>
              )}
            />

            <Typography color="textSecondary" variant="caption" sx={{ marginTop: 2, display: 'block' }}>
              You can enter one of your recovery codes in case you lost access to your mobile device.
            </Typography>
          </>
        )}
      </DialogContent>

      {captchaDisplayed && <GoogleReCaptcha onVerify={handleCaptchaVerify} />}
    </Dialog>
  )
}

const passwordSchema = z.object({
  password: z.string({ message: 'Password is required' }).min(1, 'Password is required'),
})

const otpSchema = z.object({
  otp: z.string({ message: 'OTP code is required' }).min(1, 'OTP code is required'),
})

const recoverySchema = z.object({
  recoveryCode: z.string({ message: 'Recovery code is required' }).min(1, 'Recovery code is required'),
})

const passwordFields = {
  password: {
    fieldProps: {
      autoComplete: 'current-password',
      autoFocus: true,
      label: 'Password',
      name: 'password',
      required: true,
      type: 'password',
    },
    type: 'password',
  },
} as const

const otpFields = {
  otp: {
    fieldProps: {
      autoFocus: true,
      label: 'Code',
      name: 'otp',
      required: true,
      type: 'text',
    },
    type: 'text',
  },
} as const

const recoveryFields = {
  recoveryCode: {
    fieldProps: {
      autoFocus: true,
      label: 'Code',
      name: 'recoveryCode',
      required: true,
      type: 'text',
    },
    type: 'text',
  },
} as const

const defaultPasswordValues = {
  password: '',
}

const defaultOTPValues = {
  otp: '',
}

const defaultRecoveryValues = {
  recoveryCode: '',
}

export default ReAuthDialog
